import type { Component } from 'solid-js'
import { Show } from 'solid-js'
import { Trans } from '@mbarzda/solid-i18next'
import type { MRRData } from '../../../features/revenue/domain/models/mrr'
import { DataType } from '../../../features/revenue/domain/models/mrr'
import { useNumberFormatter } from '../../../shared/helpers/currency-formatter'
import ComparisonTable from './ComparisonTable'
import ComparisonTableRow, { ComparisonTableRowStyle } from './ComparisonTableRow'

interface UsersTableProps {
  data: MRRData[]
  currentMonth?: string
  highlightMonth?: string
  formatHeader?: (dateStr: string) => string
}

const UsersTable: Component<UsersTableProps> = (props) => {
  const { currencyFormatter, currencyWithSignFormatter, numberFormatter, numberWithSignFormatter } =
    useNumberFormatter()

  // Derive dataType from data to prevent double updates
  const isEconomics = () => props.data?.[0]?.data_type === DataType.Economics

  const formatValue = (value: number, showSign = true): string => {
    if (isEconomics()) {
      return showSign ? currencyWithSignFormatter(value) : currencyFormatter(value, 0)
    }
    return showSign ? numberWithSignFormatter(value) ?? '' : numberFormatter(value) ?? ''
  }

  return (
    <Show
      when={props.data && props.data.length > 0}
      fallback={
        <div class="text-center text-gray-500 py-12">
          <Trans key="ls_generic_no_data" />
        </div>
      }
    >
      <ComparisonTable
        data={props.data}
        currentMonth={props.currentMonth}
        highlightMonth={props.highlightMonth}
        maxMonths={props.data.length}
        formatHeader={props.formatHeader}
        title={isEconomics() ? <Trans key="ls_churn_mrr" /> : <Trans key="ls_generic_users" />}
        getMonth={(item) => item.date}
        noDataFallback={
          <div class="text-center text-gray-500 py-12">
            <Trans key="ls_generic_no_data" />
          </div>
        }
        rowLabelCellClass="text-gray-700"
        rowLabelCellClassStrong="text-gray-900 font-semibold"
        getHeaderCellClass={({ isCurrentMonth, isLastColumn }) => {
          const base = `px-4 py-[14px] text-right text-sm ${
            isLastColumn ? 'font-semibold' : 'font-medium'
          } whitespace-nowrap text-gray-900`
          if (isCurrentMonth) {
            return `${base} border-b-2 border-[#3947F2] bg-[#EDF3FF]`
          }
          return base
        }}
        getValueCellClass={({ isCurrentMonth, isLastColumn }) => {
          const base = 'whitespace-nowrap text-sm text-right'
          const bold = isLastColumn ? ' font-semibold' : ''
          if (isCurrentMonth) {
            return `${base} text-gray-900 bg-[#EDF3FF]/30${bold}`
          }
          return `${base} text-gray-900${bold}`
        }}
      >
        {(ctx) => (
          <>
            <ComparisonTableRow
              ctx={ctx}
              title={isEconomics() ? <Trans key="ls_users_new" /> : <Trans key="ls_users_new_users" />}
              getValue={(item) => formatValue(item.new)}
            />
            <ComparisonTableRow
              ctx={ctx}
              title={<Trans key="ls_users_existing" />}
              getValue={(item) => formatValue(item.existing, false)}
            />
            <ComparisonTableRow
              ctx={ctx}
              title={<Trans key="ls_users_churn" />}
              getValue={(item) => formatValue(item.churn)}
            />

            <Show when={isEconomics()}>
              <ComparisonTableRow
                ctx={ctx}
                title={<Trans key="ls_users_net_new_mrr" />}
                titleVariant="strong"
                style={ComparisonTableRowStyle.Signed}
                getCell={(item) => {
                  const netNew = item.new + item.existing + item.churn
                  return {
                    numeric: netNew,
                    content: currencyWithSignFormatter(netNew)
                  }
                }}
              />
              <ComparisonTableRow
                ctx={ctx}
                title={<Trans key="ls_users_growth_rate" />}
                style={ComparisonTableRowStyle.Signed}
                getCell={(item) => {
                  const data = ctx.data()
                  const index = data.findIndex((d) => d.date === item.date)
                  const growthRate =
                    index > 0 && data[index - 1].total > 0
                      ? ((item.total - data[index - 1].total) / data[index - 1].total) * 100
                      : 0
                  return {
                    numeric: growthRate,
                    content: `${growthRate.toFixed(1)}%`
                  }
                }}
              />
              <ComparisonTableRow
                ctx={ctx}
                title={<Trans key="ls_churn_mrr" />}
                titleVariant="strong"
                style={ComparisonTableRowStyle.Strong}
                getValue={(item) => currencyWithSignFormatter(item.total)}
              />
              <ComparisonTableRow
                ctx={ctx}
                title={<Trans key="ls_users_arr" />}
                getValue={(item) => currencyWithSignFormatter(item.total * 12)}
              />
              <ComparisonTableRow
                ctx={ctx}
                title={<Trans key="ls_users_lifetime_value" />}
                getValue={(item) => currencyFormatter(Math.round(item.total / (item.new || 1)), 0)}
              />
            </Show>

            <Show when={!isEconomics()}>
              <ComparisonTableRow
                ctx={ctx}
                title={<Trans key="ls_users_customers" />}
                titleVariant="strong"
                style={ComparisonTableRowStyle.Strong}
                getValue={(item) => numberFormatter(item.total)}
              />
            </Show>
          </>
        )}
      </ComparisonTable>
    </Show>
  )
}

export default UsersTable
